import React, { useRef, useMemo, useState, useEffect } from "react";
import VideoEndOverlay from "./VideoEndOverlay";
import VideoFeedbackOverlay from "./VideoFeedbackOverlay";
import { isNavKey, DIRECTION } from "../../constants";

const { PREV, NEXT } = DIRECTION;

const videojs = window.videojs;

const CurrentTimeDisplay = videojs.getComponent("CurrentTimeDisplay");
class CustomCurrentTimeDisplay extends CurrentTimeDisplay {
  updateContent() {
    const time = this.player_.currentTime();

    // skip setting time to 0:00 while seeking
    if (this.player_.seeking() && time === 0) {
      return;
    }

    super.updateContent();
  }
}

// register component once
if (!videojs.getComponent("CustomCurrentTimeDisplay")) {
  videojs.registerComponent(
    "CustomCurrentTimeDisplay",
    CustomCurrentTimeDisplay
  );
}

export const VideoPlayer = ({
  videoId,
  handleNavigationNext,
  setDisableArrowControls,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const videojsPlayerStatusRef = useRef({
    isSeeking: false,
    // is true if videojs player "ended" event fires while a user is not seeking
    // and is set to false when a user re-plays the video
    hasEnded: false,
    // is true if player just finished seeking to prevent play/pause icon
    // from showing when videojs player automatically pauses/plays when seeking
    shouldSkipFeedbackIcon: false,
  });
  const [isShowingEndOverlay, setIsShowingEndOverlay] = useState(null);
  const [feedbackIconType, setFeedbackIconType] = useState(null);
  const options = useMemo(
    () => ({
      autoplay: true,
      muted: false,
      controls: true,
      controlBar: {
        children: [
          "playToggle",
          "muteToggle",
          "currentTimeDisplay", // add default component as a placeholder (note: index 2 in array)
          "timeDivider",
          "durationDisplay",
          "progressControl",
        ],
        currentTimeDisplay: false, // do not display the default component
      },
      userActions: { doubleClick: false }, // disable double click fullscreen
      responsive: true,
      fill: true,
      inactivityTimeout: 0,
      preload: "auto",
      sources: [
        {
          type: "application/x-mpegURL",
          src: `https://video.9dots.org/${videoId}/playlist.m3u8`,
        },
      ],
    }),
    [videoId]
  );

  // NOTE: player.play() is not called in this handler since it is handled in
  // the useEffect below
  const handleReplay = () => setIsShowingEndOverlay(false);

  // hasEnded player status is updated when state isShowingEndOverlay changes
  useEffect(() => {
    const player = playerRef.current;
    if (!playerRef.current) {
      return;
    }

    if (isShowingEndOverlay) {
      videojsPlayerStatusRef.current.hasEnded = true;
    } else {
      // automatically play player if video had just ended and overlay
      // is no longer showing (i.e. user is replaying video)
      if (videojsPlayerStatusRef.current.hasEnded) {
        player.play();
        videojsPlayerStatusRef.current.hasEnded = false;
      }
    }
  }, [isShowingEndOverlay]);

  // disable keys when end overlay is showing
  useEffect(() => {
    setDisableArrowControls(isShowingEndOverlay);

    // enable arrow controls on unmount
    return () => setDisableArrowControls(false);
  }, [isShowingEndOverlay, setDisableArrowControls]);

  // add key listener for left/right and space to handle
  // various interactions like nav away from video slide,
  // selection on video end overlay, and play/pause toggle
  useEffect(() => {
    function keyDownListener(e) {
      if (!playerRef.current) {
        return;
      }

      if (e.key === " ") {
        // reset is necessary if user seeks then presses space
        videojsPlayerStatusRef.current.shouldSkipFeedbackIcon = false;

        if (isShowingEndOverlay) {
          handleNavigationNext();
        } else {
          const player = playerRef.current;
          const playerIsPaused = player.paused();
          if (playerIsPaused) {
            player.play();
          } else {
            player.pause();
          }
        }
      }

      if (!isShowingEndOverlay) {
        return;
      }

      const isPrevNavKey = isNavKey(PREV, e.key);
      const isNextNavKey = isNavKey(NEXT, e.key);

      if (isNextNavKey) {
        handleNavigationNext();
        e.stopPropagation();
      } else if (isPrevNavKey) {
        handleReplay();
        e.stopPropagation();
      }
    }

    document.addEventListener("keydown", keyDownListener);

    return () => {
      document.removeEventListener("keydown", keyDownListener);
    };
  }, [handleNavigationNext, isShowingEndOverlay]);

  // initialize videojs player
  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");

      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = window.videojs(
        videoElement,
        options,
        () => {
          /** NOTE: callbacks are called async (per docs), so player reference is safe to use */
          player.addClass("vjs-normalise-time-controls");
          player.addClass("no-hover");
          // by default, videojs hides the control bar until the video starts
          // so adding this class ensures the control bar always appears, which
          // is necessary when the browser blocks autoplay w/sound which happens
          // when slides are reloaded on a video slide
          player.addClass("vjs-has-started");

          // replace the placeholder in the children array with our custom component
          const controlBar = player.getChild("controlBar");
          controlBar.addChild("CustomCurrentTimeDisplay", {}, 2);

          player.on("mousemove", () => {
            if (player.hasClass("no-hover")) {
              player.removeClass("no-hover");
            }
          });

          player.on("ended", () => {
            // show end overlay if player isn't actively seeking when "ended" event fires
            if (!videojsPlayerStatusRef.current.isSeeking) {
              setIsShowingEndOverlay(true);
            }
          });

          player.on(["play", "pause"], ({ type }) => {
            const currentTime = player.currentTime();
            // only show play/pause with user input (i.e. do no show when
            // videojs autoplays, plays/pauses while seeking, and pauses on end)
            if (
              !videojsPlayerStatusRef.current.isSeeking &&
              !videojsPlayerStatusRef.current.shouldSkipFeedbackIcon &&
              currentTime !== player.duration() &&
              currentTime !== 0
            ) {
              setFeedbackIconType(type);
            }

            videojsPlayerStatusRef.current.shouldSkipFeedbackIcon = false;
          });

          const {
            controlBar: { progressControl, playToggle, muteToggle },
          } = player;

          progressControl.seekBar.on(
            ["slideractive", "sliderinactive"],
            ({ type }) => {
              // show end overlay if slider became inactive after being active
              // and is at the end of the video
              // note: time is truncated to match display times on control bar
              setIsShowingEndOverlay(
                type === "sliderinactive" &&
                  Math.trunc(player.currentTime()) ===
                    Math.trunc(player.duration())
              );

              videojsPlayerStatusRef.current.isSeeking =
                type === "slideractive";
              videojsPlayerStatusRef.current.shouldSkipFeedbackIcon = true;
            }
          );

          // wait for click to finish, then blur (i.e. remove button outline and
          // allow one-click to gain focus)
          const handleButtonBlur = () =>
            requestAnimationFrame(() => document.activeElement?.blur());

          playToggle.on("click", () => {
            handleButtonBlur();
            setIsShowingEndOverlay(false);
          });

          muteToggle.on("click", () => {
            handleButtonBlur();
            setFeedbackIconType(player.muted() ? "mute" : "unmute");
          });
        }
      ));
    }
  }, [playerRef, videoRef, options]);

  // dispose videojs player when component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player id="video-container">
      <div ref={videoRef} id="video">
        {isShowingEndOverlay && (
          <VideoEndOverlay
            handleReplay={handleReplay}
            handleNavigationNext={handleNavigationNext}
          />
        )}
        <VideoFeedbackOverlay
          iconType={feedbackIconType}
          handleReset={() => setFeedbackIconType(null)}
        />
      </div>
    </div>
  );
};

export default VideoPlayer;
